import "./styles.scss";

import CardSearch from "components/navigation/card-search";
import { getLogoURL } from "components/utils/app";
import { useApp, useCardNav, useOutsideClick, useQueryDict } from "hooks/app";
import { useMember } from "hooks/member";
import React, { useState } from "react";
import { FiMenu } from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";
import { IoChevronDown, IoChevronUp, IoClose } from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import { logout, logoutAdminMember } from "services/user-service";

import { Badge, Collapse, Divider, List, ListItemButton } from "@mui/material";

const loggedOutItems = [
  { url: "/login", title: "Sign In" },
  { url: "/register", title: "Register" },
  { url: "/reminders/add", title: "Reminders" },
];

const accountItems = [
  { url: "/account", title: "Account Settings" },
  { url: "/email_settings", title: "Email Settings" },
  { url: "/notifications", title: "Notifications" },
  { url: "/change_password", title: "Change Pasword" },
  { url: "/payment", title: "Payment Info" },
];

const remindersItems = [
  { url: "/reminders", title: "My Reminders" },
  { url: "/address_book", title: "My Contacts" },
  { url: "/calendar", title: "My Calendar" },
];

const cardsItems = [
  { url: "/mycards/upcoming", title: "Upcoming" },
  { url: "/mycards/sent", title: "Sent" },
  { url: "/mycards/signed", title: "Signed" },
  { url: "/mycards/received", title: "Received" },
  { url: "/mycards/drafts", title: "Drafts" },
  { url: "/mycards/favorites", title: "Favorites" },
  { url: "/mycards/photo", title: "Photo Cards" },
  { url: "/mycards/gift", title: "Gift Cards" },
];

const MobileMenu = () => {
  const navigate = useNavigate();
  const [drawerAccountOpen, setDrawerAccountOpen] = useState(false);
  const [drawerRemindersOpen, setDrawerRemindersOpen] = useState(false);
  const [drawerCardsOpen, setDrawerCardsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const { showAlert } = useApp();
  const { isLoggedIn, adminMemberId, memberId, getMember, setMember } = useMember();
  const { pathname, queryDict, updateSearch, showSimpleHeader } = useQueryDict();
  const { navItems } = useCardNav();

  const closeMenu = () => {
    setOpen(false);
  };

  const closeSearch = () => {
    setSearchOpen(false);
  };

  const onItemClick = () => {
    closeMenu();
    closeSearch();
  };

  const onAdminMemberLogout = async () => {
    const { errors } = await logoutAdminMember();
    if (errors?.length > 0) {
      showAlert({ message: "There was a problem logging out admin", type: "error" });
    } else if (memberId) {
      getMember();
      window.location.href = `/admin/member/${memberId}`;
    }
  };

  const onLogout = async () => {
    const { errors } = await logout();
    if (errors?.length > 0) {
      showAlert({ message: "There was a problem logging you out", type: "error" });
    } else {
      setMember({});
      showAlert({ message: "You have been succesfully logged out!" });
    }
  };

  const onSearchChange = (value) => {
    const queryParams = { search_tx: value };
    navigate(`${pathname}${updateSearch(queryParams)}`);
  };

  const toggleAccountDrawer = () => {
    setDrawerAccountOpen(!drawerAccountOpen);
  };

  const toggleRemindersDrawer = () => {
    setDrawerRemindersOpen(!drawerRemindersOpen);
  };

  const toggleCardsDrawer = () => {
    setDrawerCardsOpen(!drawerCardsOpen);
  };

  const ref = useOutsideClick(closeMenu);
  const searchRef = useOutsideClick(closeSearch);

  return (
    <>
      {pathname === "/" && (
        <div className="header-search" onClick={() => setSearchOpen(true)}>
          <Badge badgeContent=" " variant="dot" invisible={!queryDict.search_tx} color="primary">
            <IoIosSearch className="search-icon" />
          </Badge>
        </div>
      )}
      {!showSimpleHeader && (
        <div className="header-burger" onClick={() => setOpen(true)}>
          <FiMenu className="burger-icon" />
        </div>
      )}
      {(open || searchOpen) && <div className="mobile-menu-backdrop" />}
      <nav ref={ref} className={`mobile-menu-nav ${open ? "open" : ""}`}>
        <div className="mobile-menu-logo">
          <img src={getLogoURL()} alt="Birthday Alarm Logo" width="189" height="31" />
          <IoClose className="mobile-menu-close" onClick={closeMenu} />
        </div>
        <Divider className="tight-top" />
        <List disablePadding>
          {isLoggedIn ? (
            <>
              <ListItemButton component="li" role="listitem" onClick={toggleAccountDrawer}>
                <span className="mobile-menu-title">My Account</span>
                {drawerAccountOpen ? <IoChevronUp className="mobile-menu-toggle" /> : <IoChevronDown className="mobile-menu-toggle" />}
              </ListItemButton>
              <Collapse in={drawerAccountOpen} component="li">
                <List disablePadding className="mobile-menu-subitems-list">
                  {accountItems.map((item, i) => (
                    <ListItemButton component="li" role="listitem" onClick={closeMenu} divider={i < accountItems.length} key={`account-item-${i}`}>
                      <Link className="mobile-menu-title" to={item.url} title={item.title}>
                        {item.title}
                      </Link>
                    </ListItemButton>
                  ))}
                  <ListItemButton component="li" role="listitem" onClick={closeMenu}>
                    {adminMemberId ? (
                      <span className="mobile-menu-title" onClick={onAdminMemberLogout}>
                        Back to admin
                      </span>
                    ) : (
                      <span className="mobile-menu-title" onClick={onLogout}>
                        Sign Out
                      </span>
                    )}
                  </ListItemButton>
                </List>
              </Collapse>
              <ListItemButton component="li" role="listitem" onClick={toggleRemindersDrawer}>
                <span className="mobile-menu-title">My Reminders</span>
                {drawerRemindersOpen ? <IoChevronUp className="mobile-menu-toggle" /> : <IoChevronDown className="mobile-menu-toggle" />}
              </ListItemButton>
              <Collapse in={drawerRemindersOpen} component="li">
                <List disablePadding className="mobile-menu-subitems-list">
                  {remindersItems.map((item, i) => (
                    <ListItemButton component="li" role="listitem" onClick={closeMenu} divider={i < remindersItems.length - 1} key={`reminders-item-${i}`}>
                      <Link className="mobile-menu-title" to={item.url} title={item.title}>
                        {item.title}
                      </Link>
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
              <ListItemButton component="li" role="listitem" onClick={toggleCardsDrawer}>
                <span className="mobile-menu-title">My Cards</span>
                {drawerCardsOpen ? <IoChevronUp className="mobile-menu-toggle" /> : <IoChevronDown className="mobile-menu-toggle" />}
              </ListItemButton>
              <Collapse in={drawerCardsOpen} component="li">
                <List disablePadding className="mobile-menu-subitems-list">
                  {cardsItems.map((item, i) => (
                    <ListItemButton component="li" role="listitem" onClick={closeMenu} divider={i < cardsItems.length - 1} key={`cards-item-${i}`}>
                      <Link className="mobile-menu-title" to={item.url} title={item.title}>
                        {item.title}
                      </Link>
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </>
          ) : (
            <>
              {loggedOutItems.map((item, i) => (
                <ListItemButton component="li" role="listitem" onClick={closeMenu} key={`cards-item-${i}`}>
                  <Link className="mobile-menu-title" to={item.url} title={item.title}>
                    {item.title}
                  </Link>
                </ListItemButton>
              ))}
            </>
          )}
          <Divider component="li" role="listitem" />
          <div className="mobile-menu-section-title">BROWSE CARDS</div>
          {navItems?.map((item) => (
            <ListItemButton component="li" role="listitem" onClick={onItemClick} key={`mobile-menu-item-${item.cardCategoryId}`}>
              <Link className="mobile-menu-title" to={item.fullCardCategorySlug} alt={item.shortCategoryName} title={item.shortCategoryName}>
                {item.shortCategoryName}
              </Link>
            </ListItemButton>
          ))}
        </List>
      </nav>
      <div ref={searchRef} className={`mobile-search ${searchOpen ? "open" : ""}`}>
        <div className="mobile-search-header">
          <div className="card-category-search">
            <CardSearch onSearchChange={onSearchChange} placeholder="Search all cards" />
          </div>
          <IoClose className="mobile-search-close" onClick={closeSearch} />
        </div>
        <Divider />
        <div className="mobile-menu-section-title">BROWSE CARDS</div>
        <div className="mobile-search-categories">
          {navItems?.map((item) => (
            <ListItemButton component="li" role="listitem" onClick={onItemClick} key={`search-menu-item-${item.cardCategoryId}`}>
              <Link className="mobile-menu-title" to={item.fullCardCategorySlug} alt={item.shortCategoryName} title={item.shortCategoryName}>
                {item.shortCategoryName}
              </Link>
            </ListItemButton>
          ))}
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
